/**
 * LongChat Widget Entry Point
 *
 * Exposes the global LongChat API on window object.
 * This is the main entry point for the widget bundle.
 */

import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { WidgetApp } from './WidgetApp';
import type { LongChatInitOptions, LongChatGlobal } from './types/widget';
import widgetCss from './styles/widget.css?inline';

// Widget state
let root: Root | null = null;
let container: HTMLDivElement | null = null;
let hostElement: HTMLDivElement | null = null;
let shadowRootRef: ShadowRoot | null = null;
let currentOptions: LongChatInitOptions | null = null;
let isInitialized = false;

// Store callbacks for external control
let openCallback: (() => void) | null = null;
let closeCallback: (() => void) | null = null;
let sendMessageCallback: ((text: string) => Promise<void>) | null = null;

/**
 * Inject styles into shadow root
 */
function injectStylesIntoShadow(shadowRoot: ShadowRoot): void {
  const existingStyle = shadowRoot.querySelector('style[data-longchat-styles="true"]');
  if (existingStyle) return;

  const styleTag = document.createElement('style');
  styleTag.setAttribute('data-longchat-styles', 'true');
  styleTag.textContent = widgetCss;
  shadowRoot.appendChild(styleTag);
}

/**
 * Create the widget container element inside Shadow DOM
 */
function createContainer(): HTMLDivElement {
  const existingHost = document.getElementById('longchat-widget-host') as HTMLDivElement | null;

  if (existingHost?.shadowRoot) {
    const existingMount = existingHost.shadowRoot.getElementById('longchat-widget-root');
    if (existingMount) {
      hostElement = existingHost;
      shadowRootRef = existingHost.shadowRoot;
      return existingMount as HTMLDivElement;
    }
  }

  hostElement = document.createElement('div');
  hostElement.id = 'longchat-widget-host';
  hostElement.style.all = 'initial';
  hostElement.style.position = 'static';

  document.body.appendChild(hostElement);

  shadowRootRef = hostElement.attachShadow({ mode: 'open' });
  injectStylesIntoShadow(shadowRootRef);

  const mountPoint = document.createElement('div');
  mountPoint.id = 'longchat-widget-root';
  shadowRootRef.appendChild(mountPoint);

  return mountPoint;
}

/**
 * Inject Heebo font if not already present
 */
function injectFont(): void {
  const existingLink = document.querySelector(
      'link[href*="fonts.googleapis.com"][href*="Heebo"]'
  );
  if (existingLink) return;

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href =
      'https://fonts.googleapis.com/css2?family=Heebo:wght@400;500;600;700&display=swap';
  document.head.appendChild(link);
}

/**
 * Parse string boolean from data-* attributes
 */
function parseBoolean(value?: string): boolean | undefined {
  if (value == null || value === '') return undefined;
  return value === 'true';
}

/**
 * Parse and clamp numeric data-* values.
 * Invalid values are ignored so existing/default behavior stays intact.
 */
function parseClampedNumber(
    value: string | null,
    min: number,
    max: number
): number | undefined {
  if (value == null || value.trim() === '') return undefined;

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return undefined;

  return Math.min(max, Math.max(min, Math.round(parsed)));
}

/**
 * Get the current widget script tag
 */
function getCurrentScriptTag(): HTMLScriptElement | null {
  if (document.currentScript instanceof HTMLScriptElement) {
    return document.currentScript;
  }

  const scripts = Array.from(document.querySelectorAll('script'));
  const matched = scripts.filter((script) =>
      (script as HTMLScriptElement).src.includes('longchat-widget')
  );

  return (matched[matched.length - 1] as HTMLScriptElement) || null;
}

function getAttr(script: HTMLScriptElement, name: string): string | null {
  return script.getAttribute(name);
}

/**
 * Read init options from the script tag's data-* attributes
 *
 * New supported fields:
 * - data-first-name
 * - data-user-contact-information
 * - data-user-context
 * - data-button-size
 * - data-offset-x
 * - data-offset-y
 */
function readOptionsFromScriptTag(): LongChatInitOptions | null {
  const script = getCurrentScriptTag();
  if (!script) return null;

  const accountId = getAttr(script, 'data-account-id');
  const agentId = getAttr(script, 'data-agent-id');

  if (!accountId || !agentId) {
    return null;
  }

  const theme: LongChatInitOptions['theme'] = {};
  const user: LongChatInitOptions['user'] = {};
  const context: LongChatInitOptions['context'] = {};

  const primaryColor = getAttr(script, 'data-primary-color');
  const position = getAttr(script, 'data-position');
  const fullscreen = getAttr(script, 'data-fullscreen');
  const lang = getAttr(script, 'data-lang');
  const buttonSize = parseClampedNumber(getAttr(script, 'data-button-size'), 40, 96);
  const offsetX = parseClampedNumber(getAttr(script, 'data-offset-x'), 0, 200);
  const offsetY = parseClampedNumber(getAttr(script, 'data-offset-y'), 0, 200);

  const firstName = getAttr(script, 'data-first-name');
  const userContactInformation = getAttr(script, 'data-user-contact-information');
  const userContext = getAttr(script, 'data-user-context');
  const userExternalId = getAttr(script, 'data-user-external-id');

  if (primaryColor) {
    theme.primaryColor = primaryColor;
  }

  if (
      position === 'bottom-left' ||
      position === 'bottom-right' ||
      position === 'top-left' ||
      position === 'top-right'
  ) {
    theme.position = position;
  }

  if (buttonSize !== undefined) {
    theme.buttonSize = buttonSize;
  }

  if (offsetX !== undefined) {
    theme.offsetX = offsetX;
  }

  if (offsetY !== undefined) {
    theme.offsetY = offsetY;
  }

  if (firstName) {
    user.firstName = firstName;
  }

  if (userContactInformation) {
    user.contactInformation = userContactInformation;
  }

  if (userExternalId) {
    user.externalId = userExternalId;
  }

  if (userContext) {
    context.userContext = userContext;
  }

  const options: LongChatInitOptions = {
    accountId,
    agentId,
    fullscreen: parseBoolean(fullscreen || undefined),
    lang: lang === 'he' || lang === 'en' ? lang : undefined,
    theme: Object.keys(theme).length ? theme : undefined,
    user: Object.keys(user).length ? user : undefined,
    context: Object.keys(context).length ? context : undefined,
  };

  return options;
}

/**
 * Auto-initialize from <script data-*> tag
 */
function autoInitFromScriptTag(): void {
  if (isInitialized) return;

  const autoOptions = readOptionsFromScriptTag();
  if (!autoOptions) return;

  init(autoOptions);
}

/**
 * Initialize the widget
 */
function init(options: LongChatInitOptions): void {
  if (isInitialized) {
    console.warn(
        '[LongChat] Widget already initialized. Call destroy() first to reinitialize.'
    );
    return;
  }

  if (!options.accountId) {
    console.error('[LongChat] accountId is required');
    return;
  }
  if (!options.agentId) {
    console.error('[LongChat] agentId is required');
    return;
  }

  console.log('[LongChat] Initializing widget...', options);

  currentOptions = options;

  injectFont();

  container = createContainer();

  root = createRoot(container);

  root.render(
      React.createElement(WidgetApp, {
        options,
      })
  );

  isInitialized = true;
  console.log('[LongChat] Widget initialized successfully');
}

/**
 * Open the chat window
 */
function open(): void {
  if (!isInitialized) {
    console.warn('[LongChat] Widget not initialized. Call init() first.');
    return;
  }
  if (openCallback) {
    openCallback();
  }
}

/**
 * Close the chat window
 */
function close(): void {
  if (!isInitialized) {
    console.warn('[LongChat] Widget not initialized. Call init() first.');
    return;
  }
  if (closeCallback) {
    closeCallback();
  }
}

/**
 * Send a message programmatically
 */
async function sendMessage(text: string): Promise<void> {
  if (!isInitialized) {
    console.warn('[LongChat] Widget not initialized. Call init() first.');
    return;
  }
  if (!text || !text.trim()) {
    console.warn('[LongChat] Cannot send empty message');
    return;
  }
  if (sendMessageCallback) {
    await sendMessageCallback(text);
  }
}

/**
 * Destroy the widget and clean up
 */
function destroy(): void {
  if (!isInitialized) {
    return;
  }

  if (root) {
    root.unmount();
    root = null;
  }

  if (hostElement && hostElement.parentNode) {
    hostElement.parentNode.removeChild(hostElement);
  }

  container = null;
  hostElement = null;
  shadowRootRef = null;
  currentOptions = null;
  isInitialized = false;
  openCallback = null;
  closeCallback = null;
  sendMessageCallback = null;

  console.log('[LongChat] Widget destroyed');
}

/**
 * Check if widget is initialized
 */
function checkInitialized(): boolean {
  return isInitialized;
}

// Create the global API
const LongChat: LongChatGlobal = {
  init,
  open,
  close,
  sendMessage,
  destroy,
  isInitialized: checkInitialized,
};

// Expose on window
if (typeof window !== 'undefined') {
  window.LongChat = LongChat;

  if (document.readyState === 'loading') {
    window.addEventListener('DOMContentLoaded', autoInitFromScriptTag);
  } else {
    autoInitFromScriptTag();
  }
}

// Export for module usage
export { LongChat };
export default LongChat;